import { ApiClientError } from '@/lib/api/client'

type Budget = { attempts: number[]; blockedUntil: number }
const memory = new Map<string, Budget>()
const active = new Set<string>()
const WINDOW_MS = 60_000

function groupFor(endpoint: string, method: string): string | null {
  if (method.toUpperCase() !== 'POST') return null
  const path = endpoint.split('?')[0]
  if (
    /^\/api\/donor\/(?:campaign-account\/)?(?:register|login|check-phone|(?:phone-access|email-access|first-access)\/(?:check|login|define-password)|password\/(?:forgot|forgot-by-phone|reset))$/.test(
      path
    )
  )
    return 'auth'
  if (/^\/api\/campaigns\/[^/]+\/payments\/pix$/.test(path)) return 'checkout'
  if (/^\/api\/scratch-campaigns\/[^/]+\/register$/.test(path)) return 'register'
  return null
}

function read(group: string): Budget {
  let budget = memory.get(group) ?? { attempts: [], blockedUntil: 0 }
  try {
    const raw: unknown = JSON.parse(
      localStorage.getItem(`lp-public-budget:${group}`) ?? 'null'
    )
    if (raw === null) budget = { attempts: [], blockedUntil: 0 }
    if (
      raw &&
      typeof raw === 'object' &&
      'attempts' in raw &&
      Array.isArray(raw.attempts) &&
      raw.attempts.every(
        (item: unknown) => typeof item === 'number' && Number.isFinite(item)
      ) &&
      'blockedUntil' in raw &&
      typeof raw.blockedUntil === 'number' &&
      Number.isFinite(raw.blockedUntil)
    ) {
      budget = { attempts: raw.attempts, blockedUntil: raw.blockedUntil }
    }
  } catch {
    /* Memory fallback when browser storage is unavailable. */
  }
  return budget
}

function save(group: string, budget: Budget): void {
  memory.set(group, budget)
  try {
    localStorage.setItem(`lp-public-budget:${group}`, JSON.stringify(budget))
  } catch {
    /* Memory fallback. */
  }
}

/** UX protection only; the API independently enforces its own budgets. Stores no identity data. */
export function beginPublicRequest(endpoint: string, method = 'GET'): () => void {
  const group = groupFor(endpoint, method)
  if (!group) return () => undefined
  const now = Date.now()
  const budget = read(group)
  budget.attempts = budget.attempts.filter((time) => time > now - WINDOW_MS)
  const max = group === 'auth' ? 10 : 5
  if (active.has(group) || budget.blockedUntil > now || budget.attempts.length >= max) {
    const until = Math.max(budget.blockedUntil, (budget.attempts[0] ?? now) + WINDOW_MS)
    throw new ApiClientError(
      `Muitas tentativas. Aguarde ${Math.max(1, Math.ceil((until - now) / 1000))} segundos e tente novamente.`,
      429
    )
  }
  budget.attempts.push(now)
  save(group, budget)
  active.add(group)
  return () => {
    active.delete(group)
  }
}

export function blockPublicRequests(
  endpoint: string,
  method: string,
  retryAfter: string | null
): void {
  const group = groupFor(endpoint, method)
  if (!group) return
  const seconds = retryAfter && /^\d+$/.test(retryAfter) ? Number(retryAfter) : 0
  const date = retryAfter ? Date.parse(retryAfter) : NaN
  const delay =
    seconds > 0 ? seconds * 1000 : Number.isFinite(date) ? date - Date.now() : WINDOW_MS
  const budget = read(group)
  budget.blockedUntil = Math.max(budget.blockedUntil, Date.now() + Math.max(1000, delay))
  save(group, budget)
}
